import { css } from 'aphrodite';
import { useMemo, useState } from 'react';
import { FormState } from 'react-hook-form';
import { Input } from '../../../../../web/components-v2/Input';
import { IAddress, IDonorInfoCollectionOptions } from '../../../../models/Api';
import { IFormValues } from '../utils';
import { AddressInput } from './AddressInput';
import { ErrorMessage } from './ErrorMessage';
import { styleSheet } from './style';

type Props = {
	firstName: string;
	lastName: string;
	donorName: string;
	email: string;
	phone: string;
	address: IAddress;
	collectDonorPhone: IDonorInfoCollectionOptions;
	collectDonorAddress: IDonorInfoCollectionOptions;
	setValue: (name: keyof IFormValues, value: string | IAddress) => void;
	formState?: FormState<IFormValues>;
};

export const CustomerDataCollection = ({
	firstName,
	lastName,
	donorName,
	email,
	phone,
	address,
	collectDonorPhone,
	collectDonorAddress,
	setValue,
	formState,
}: Props) => {
	const [isDonorNameFocused, setIsDonorNameFocused] = useState(false);
	const derivedDonorName = [firstName, lastName].filter(Boolean).join(' ');

	const showPhone = useMemo(() => {
		return collectDonorPhone.collect || collectDonorPhone.require;
	}, [collectDonorPhone]);

	const showAddress = useMemo(() => {
		return collectDonorAddress.collect || collectDonorAddress.require;
	}, [collectDonorAddress]);

	return (
		<div className={css(styleSheet.container)}>
			<div className={css(styleSheet.customerInfoContainer)}>
				<div className={css(styleSheet.inputContainer)}>
					<label htmlFor='donation-survey-customer-first-name'>
						First Name <span className={css(styleSheet.required)}>*</span>
					</label>

					<Input
						id='donation-survey-customer-first-name'
						value={firstName}
						onChange={e => setValue('firstName', e.target.value)}
					/>
					{formState?.errors?.firstName && <ErrorMessage message={formState.errors.firstName.message} />}
				</div>

				<div className={css(styleSheet.inputContainer)}>
					<label htmlFor='donation-survey-customer-last-name'>
						Last Name <span className={css(styleSheet.required)}>*</span>
					</label>

					<Input
						id='donation-survey-customer-last-name'
						value={lastName}
						onChange={e => setValue('lastName', e.target.value)}
					/>
					{formState?.errors?.lastName && <ErrorMessage message={formState.errors.lastName.message} />}
				</div>

				<div className={css(styleSheet.inputContainer)}>
					<label htmlFor='donation-survey-customer-donor-name'>Donor Name / Company</label>

					<Input
						id='donation-survey-customer-donor-name'
						value={isDonorNameFocused ? donorName : donorName || derivedDonorName}
						onChange={e => setValue('donorName', e.target.value)}
						onFocus={() => {
							setIsDonorNameFocused(true);
							// materialize the derived default so it can be edited in place instead of vanishing
							if (!donorName && derivedDonorName) {
								setValue('donorName', derivedDonorName);
							}
						}}
						onBlur={() => {
							setIsDonorNameFocused(false);
							// blank or unchanged-from-default resumes live-tracking the name fields
							if (!donorName.trim() || donorName.trim() === derivedDonorName) {
								setValue('donorName', '');
							}
						}}
					/>
				</div>

				<div className={css(styleSheet.inputContainer)}>
					<label htmlFor='donation-survey-customer-email'>
						Email <span className={css(styleSheet.required)}>*</span>
					</label>
					<Input id='donation-survey-customer-email' value={email} onChange={e => setValue('email', e.target.value)} />
					{formState?.errors?.email && <ErrorMessage message={formState.errors.email.message} />}
				</div>

				{showPhone && (
					<div className={css(styleSheet.inputContainer)}>
						<label htmlFor='donation-survey-customer-phone'>
							Phone {collectDonorPhone.require && <span className={css(styleSheet.required)}>*</span>}
						</label>
						<Input
							id='donation-survey-customer-phone'
							value={phone}
							onChange={e => setValue('phone', e.target.value)}
						/>
						{formState?.errors?.phone && <ErrorMessage message={formState.errors.phone.message} />}
					</div>
				)}
			</div>
			{showAddress && (
				<div className={css(styleSheet.addressContainer)}>
					<AddressInput
						address={address}
						collectDonorAddress={collectDonorAddress}
						onAddressChange={updatedAddress => setValue('address', updatedAddress)}
						formState={formState}
					/>
				</div>
			)}
		</div>
	);
};
